import { UP_TO_5_MONTHS, UP_TO_11_MONTHS } from '../../../constants/quoteFlow';
import { formatCurrency } from '../../../shared/utils';

export const PET_CHANGE_OPTIONS_WITHOUT_WELLNESS = [
  { id: 'midterm', name: 'Mid-Term Change' },
  { id: 'renewal', name: 'Renewal Change' },
  { id: 'details', name: 'Edit Pet Details' },
];

export const PET_CHANGE_OPTIONS = [
  { id: 'midterm', name: 'Mid-Term Change' },
  { id: 'renewal', name: 'Renewal Change' },
  { id: 'wellness_renewal', name: 'Renewal Change - Pumpkin Wellness Club' },
  { id: 'details', name: 'Edit Pet Details' },
];

export const ADJUST_CLAIM_UTILIZATION_OPTION = {
  id: 'adjust_claim_utilization',
  name: 'Adjust Pet Claim Utilization',
};

export function getPetChangeOptions(petData, isAdjustClaimUtilizationEnabled) {
  const options = petData?.wellness?.group_enrolled
    ? PET_CHANGE_OPTIONS_WITHOUT_WELLNESS
    : PET_CHANGE_OPTIONS;

  return isAdjustClaimUtilizationEnabled
    ? [...options, ADJUST_CLAIM_UTILIZATION_OPTION]
    : options;
}

export function formatPetEditTitle(petData, customerData) {
  return petData && customerData
    ? `${customerData.first_name} ${customerData.last_name} - ${customerData.pumpkin_id} - ${petData.name}`
    : ' ';
}

export function addIdToMidtermOptions(midtermChangeData) {
  return midtermChangeData.map((option, index) => {
    option.id = String(index + 1);
    return option;
  });
}

export function sortMidtermOptions(midtermChangeData) {
  return midtermChangeData.sort((a, b) =>
    // Sort by deductible first: 100, 250, 500, 1000
    // When sorting by deductible, we need to compare the int value, otherwise the result would be: 100, 1000, 250, 500;
    // Then we sort by annual limit: 7000, 10000, 15000, 20000, Unlimited
    // When annual_limit === UNLIMITED, we use the MAX INT to guarantee this options to be pushed to the end;
    a.deductible === b.deductible
      ? (a.annual_limit.toUpperCase() === 'UNLIMITED'
          ? Number.MAX_SAFE_INTEGER
          : parseInt(a.annual_limit)) -
        (b.annual_limit.toUpperCase() === 'UNLIMITED'
          ? Number.MAX_SAFE_INTEGER
          : parseInt(b.annual_limit))
      : parseInt(a.deductible) - parseInt(b.deductible)
  );
}

export function groupMidtermData(midtermChangeData) {
  return midtermChangeData
    .filter(item => item.allowed)
    .reduce((data, item) => {
      const { coinsurance, deductible } = item;

      if (!data[coinsurance]) {
        data[coinsurance] = {};
      }

      if (!data[coinsurance][deductible]) {
        data[coinsurance][deductible] = [];
      }

      data[coinsurance][deductible].push(item);

      return data;
    }, {});
}

export function getPetSpecies(pet) {
  const { species, age } = pet || {};

  if (species === 'cat' && (age === UP_TO_5_MONTHS || age === UP_TO_11_MONTHS)) {
    return 'kitten';
  }

  if (species === 'dog' && (age === UP_TO_5_MONTHS || age === UP_TO_11_MONTHS)) {
    return 'puppy';
  }

  return species;
}

export function formatCurrentPolicyText(petData) {
  const currentReimbursementRate = 100 - petData.coinsurance;
  const currentAnnualLimit = formatCurrency(parseInt(petData.annual_limit) / 1000, {
    areCents: false,
    hideZero: true,
    ifNaN: 'Unlimited',
  });

  return `Current Insurance Policy: ${currentReimbursementRate}% Reimbursement Rate / $${
    petData.deductible
  } deductible / ${
    currentAnnualLimit !== 'Unlimited' ? currentAnnualLimit + 'K' : 'Unlimited'
  } annual limit = $${petData.filed_rate}/month`;
}

function groupChangeDataByAnnualLimit(changeData) {
  return changeData
    .filter(item => item.allowed)
    .sort((a, b) => {
      if (a.annual_limit === 'Unlimited') {
        return 1; // push unlimited to the end
      }
      return a.annual_limit - b.annual_limit;
    })
    .reduce((data, item) => {
      const { annual_limit } = item;

      if (!data[annual_limit]) {
        data[annual_limit] = [];
      }

      data[annual_limit].push(item);
      return data;
    }, {});
}

export function getChangeOptionsCopyText(changeData, petData) {
  const clipboardText = [];

  clipboardText.push(formatCurrentPolicyText(petData));

  // group policy change options by annual limit
  const orderedOptions = groupChangeDataByAnnualLimit(changeData);

  for (const key in orderedOptions) {
    // order policy change options, first by reimbursement rate, then by deductible
    const options = orderedOptions[key].sort((a, b) => {
      return a.coinsurance - b.coinsurance || b.deductible - a.deductible;
    });

    const annualLimit = formatCurrency(parseInt(key) / 1000, {
      hideZero: true,
      areCents: false,
      ifNaN: 'Unlimited',
    });

    if (annualLimit !== 'Unlimited') {
      clipboardText.push(`\n${annualLimit}K annual limit`);
    } else {
      clipboardText.push(`\nUnlimited annual limit`);
    }

    // add string to result for each option
    options.forEach(option => {
      const reimbursementRate = 100 - option.coinsurance;
      const deductible = option.deductible;
      const pricePerMonth = option.filed_rate;
      clipboardText.push(
        `${reimbursementRate}% Reimbursement Rate / $${deductible} deductible = $${pricePerMonth}/month`
      );
    });
  }

  // turn clipboardText into a single string
  return clipboardText.join('\n');
}

export function getWellnessPrice(wellness, isChargedAnnually) {
  if (!wellness || Object.keys(wellness).length === 0) {
    return 0;
  }

  const {
    vendor_price: vendorPrice,
    price,
    discount_offers_amount: discountOffersAmount = 0,
  } = wellness || {};

  const effectivePrice = vendorPrice > 0 ? vendorPrice : price;

  const planPrice = isChargedAnnually
    ? effectivePrice * getWellnessPriceMultiplier(wellness)
    : effectivePrice;

  return discountOffersAmount > 0 ? planPrice - discountOffersAmount : planPrice;
}

export function getWellnessPriceMultiplier(wellness) {
  const { discount_offers_amount: hasDiscountOffersAmount, program_id: programId } =
    wellness || {};

  const partner = programId?.split('-')[2];

  return hasDiscountOffersAmount > 0 ||
    ['airvetgrooming', 'airvet'].includes(partner)
    ? 12
    : 11;
}

export function validateWellnessRenewalChange(petData, changeType) {
  if (changeType === 'wellness_renewal' && petData?.wellness.group_enrolled) {
    return {
      valid: false,
      message:
        'Renewal change can not be completed. This pet is from a group enrolled wellness.',
    };
  }

  if (
    changeType === 'wellness_renewal' &&
    petData &&
    !petData.wellness_plans.some(plan => plan.status === 'renew_pending')
  ) {
    return {
      valid: false,
      message:
        'Renewal change can not be completed. This pet is not within the renewal window.',
    };
  }

  return { valid: true, message: '' };
}

export function getWellnessPlanToShowUtilization(wellness_plans = []) {
  return (
    wellness_plans.find(plan => plan.status === 'active') ||
    wellness_plans.find(plan => plan.status === 'pending') ||
    wellness_plans.find(plan => plan.status === 'cancel_pending') ||
    wellness_plans.find(plan => plan.status === 'cancelled') ||
    wellness_plans.find(plan => plan.status === 'expired') ||
    wellness_plans[0]
  );
}
